import { getFirebaseStorage } from "@/lib/firebase";
import { ref, uploadBytesResumable, deleteObject, type UploadMetadata } from "firebase/storage";
import { sanitizeFilename } from "@/lib/filename-utils";

export interface ImageUploadResult {
  storagePath: string;
  fileName: string;
}

export interface ImageUploadOptions {
  /** Photo date extracted from EXIF (ISO 8601 string) */
  photoDate?: string;
}

/**
 * Get file extension from MIME type
 */
function getExtensionFromMimeType(mimeType: string): string {
  if (mimeType.includes("jpeg") || mimeType.includes("jpg")) return "jpg";
  if (mimeType.includes("png")) return "png";
  if (mimeType.includes("gif")) return "gif";
  if (mimeType.includes("webp")) return "webp";
  return "jpg"; // Default fallback
}

/**
 * Upload image file to Firebase Storage with progress callback
 *
 * @param imageFile - The image file to upload
 * @param userId - User ID for storage path
 * @param conversationId - Conversation ID for storage path
 * @param onProgress - Optional callback for upload progress (0-100)
 * @param originalFileName - Optional original filename to preserve (will be sanitized)
 * @param options - Optional metadata like photoDate
 * @returns Upload result with storage path and download URL
 */
export async function uploadImageToStorage(
  imageFile: File | Blob,
  userId: string,
  conversationId: string,
  onProgress?: (progress: number) => void,
  originalFileName?: string,
  options?: ImageUploadOptions,
): Promise<ImageUploadResult> {
  const storage = getFirebaseStorage();

  const mimeType =
    imageFile instanceof File ? imageFile.type : "image/jpeg";
  const extension = getExtensionFromMimeType(mimeType);
  const fileName = sanitizeFilename(originalFileName || `image.${extension}`);
  const storagePath = `users/${userId}/projects/${conversationId}/images/${fileName}`;

  const storageRef = ref(storage, storagePath);

  // Build upload metadata with optional custom metadata
  const uploadMetadata: UploadMetadata = {
    contentType: mimeType,
  };

  // Add photo date to custom metadata if available
  if (options?.photoDate) {
    uploadMetadata.customMetadata = {
      photoDate: options.photoDate,
    };
  }

  return new Promise((resolve, reject) => {
    const uploadTask = uploadBytesResumable(storageRef, imageFile, uploadMetadata);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        onProgress?.(Math.round(progress));
      },
      (error) => {
        reject(error);
      },
      () => {
        resolve({
          storagePath: `images/${fileName}`,
          fileName,
        });
      },
    );
  });
}

/**
 * Delete image file from Firebase Storage
 *
 * @param userId - User ID for storage path
 * @param conversationId - Conversation ID for storage path
 * @param fileName - The file name to delete
 */
export async function deleteImageFromStorage(
  userId: string,
  conversationId: string,
  fileName: string,
): Promise<void> {
  const storage = getFirebaseStorage();
  const storagePath = `users/${userId}/projects/${conversationId}/images/${fileName}`;
  const storageRef = ref(storage, storagePath);

  await deleteObject(storageRef);
}
